import { fromEvent, Subject } from 'rxjs';
import { takeUntil, debounceTime, throttleTime, filter } from 'rxjs/operators';
import { TOOLTIP_DEFAULTS, TooltipEvent } from './tooltip.config';

// ✅ Servicio para gestión de eventos del tooltip
export class TooltipEventManager {
  
  /**
   * Configura eventos de mouse para dispositivos desktop
   */
  static setupMouseEvents(
    element: HTMLElement,
    destroy$: Subject<void>,
    eventHandler: (eventType: TooltipEvent, event?: Event) => void,
    contentValidator: () => boolean
  ): void {
    const mouseEnter$ = fromEvent<MouseEvent>(element, 'mouseenter');
    const mouseLeave$ = fromEvent<MouseEvent>(element, 'mouseleave');
    
    mouseEnter$.pipe(
      debounceTime(TOOLTIP_DEFAULTS.DEBOUNCE_TIME),
      filter(() => contentValidator()),
      takeUntil(destroy$)
    ).subscribe(event => eventHandler('show', event));
    
    mouseLeave$.pipe(
      debounceTime(TOOLTIP_DEFAULTS.DEBOUNCE_TIME),
      takeUntil(destroy$)
    ).subscribe(() => eventHandler('hide'));
  }
  
  /**
   * Configura eventos táctiles para dispositivos móviles
   */
  static setupTouchEvents(
    element: HTMLElement,
    destroy$: Subject<void>,
    eventHandler: (eventType: TooltipEvent, event?: Event) => void,
    contentValidator: () => boolean
  ): void {
    const touchStart$ = fromEvent<TouchEvent>(element, 'touchstart');
    
    touchStart$.pipe(
      debounceTime(TOOLTIP_DEFAULTS.DEBOUNCE_TIME * 2), // Más debounce en móvil
      filter(() => contentValidator()),
      takeUntil(destroy$)
    ).subscribe(event => {
      event.preventDefault();
      event.stopPropagation(); // Evitar propagación que cause conflictos
      eventHandler('toggle', event);
    });
  }
  
  /**
   * Configura eventos globales (scroll, resize, click outside)
   */
  static setupGlobalEvents(
    destroy$: Subject<void>,
    eventHandler: (eventType: TooltipEvent, event?: Event) => void,
    documentClickHandler: (event: Event) => void,
    cacheInvalidator: () => void,
    isVisible: () => boolean,
    isTouchDevice: boolean
  ): void {
    // Click outside para cerrar en dispositivos táctiles
    const documentClick$ = fromEvent<Event>(document, 'click');
    documentClick$.pipe(
      filter(() => isTouchDevice && isVisible()),
      takeUntil(destroy$)
    ).subscribe(event => documentClickHandler(event));
    
    // Scroll para ocultar tooltip (mejor UX)
    const scroll$ = fromEvent(window, 'scroll');
    scroll$.pipe(
      throttleTime(TOOLTIP_DEFAULTS.THROTTLE_TIME), 
      filter(() => isVisible()),
      takeUntil(destroy$)
    ).subscribe(() => {
      cacheInvalidator(); // Invalidar cache
      eventHandler('hide'); // Ocultar tooltip durante scroll
    });
    
    // Touch scroll específico para mobile (touchmove en document)
    const touchScroll$ = fromEvent(document, 'touchmove');
    touchScroll$.pipe(
      throttleTime(TOOLTIP_DEFAULTS.THROTTLE_TIME),
      filter(() => isTouchDevice && isVisible()),
      takeUntil(destroy$)
    ).subscribe(() => {
      cacheInvalidator();
      eventHandler('hide'); // Ocultar tooltip durante touch scroll
    });
    
    // Resize para invalidar cache
    const resize$ = fromEvent(window, 'resize');
    resize$.pipe(
      debounceTime(150), // Más debounce para resize
      takeUntil(destroy$)
    ).subscribe(() => {
      cacheInvalidator();
      if (isVisible()) {
        eventHandler('hide'); // Ocultar tooltip en resize también
      }
    });
  }
}
